import React, { useState } from "react";
import axios from "axios";
import {
  Container,
  TextField,
  Typography,
  Box,
  Button,
  Grid,
  RadioGroup,
  FormControlLabel,
  Radio,
  Checkbox,
  FormGroup,
  Paper,
  Divider,
} from "@mui/material";
import Navbar from "./Navbar";
import Footer from "./Footer";
import "./CourseUpload.css";
import { MenuItem, Select, InputLabel, FormControl } from "@mui/material";

const API = "https://course-selling-backend-2-rr1n.onrender.com";

const CourseUpload = () => {
  const [formData, setFormData] = useState({
    title: "",
    level: "",
    expertise: "",
    duration: "",
    mode: "",
    description: "",
    eligibility: "",
    certificate: false,
    price: "",
    discount: "",
    enrollmentLink: "",
    logoPlacement: "",
    status: "",
    thumbnail: "", // ✅ now stores image link instead of file
    videos: [""],
  });

  // ✅ Convert Google Drive links to embeddable preview format
  const handleVideoChange = (index, value) => {
    let finalLink = value.trim();
    if (finalLink.includes("drive.google.com")) {
      const fileIdMatch = finalLink.match(/\/d\/(.*?)\//);
      const fileIdParam = finalLink.match(/id=([^&]+)/);
      const fileId = fileIdMatch ? fileIdMatch[1] : fileIdParam ? fileIdParam[1] : null;
      if (fileId) finalLink = `https://drive.google.com/file/d/${fileId}/preview`;
    }
    const updated = [...formData.videos];
    updated[index] = finalLink;
    setFormData({ ...formData, videos: updated });
  };

  const addVideoField = () =>
    setFormData({ ...formData, videos: [...formData.videos, ""] });

  // ✅ Simplified handleChange (no file handling)
  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData({
      ...formData,
      [name]: type === "checkbox" ? checked : value,
    });
  };

  // ✅ Updated handleUpload (no FormData / file upload)
  const handleUpload = async (e) => {
    e.preventDefault();
    try {
      const token = localStorage.getItem("token");
      const payload = { ...formData, videos: JSON.stringify(formData.videos) };

      await axios.post(`${API}/api/courses/upload`, payload, {
        headers: {
          "x-auth-token": token,
          "Content-Type": "application/json",
        },
      });

      alert("✅ Course uploaded successfully!");
      setFormData({
        title: "",
        level: "",
        expertise: "",
        duration: "",
        mode: "",
        description: "",
        eligibility: "",
        certificate: false,
        price: "",
        discount: "",
        enrollmentLink: "",
        logoPlacement: "",
        status: "",
        thumbnail: "",
        videos: [""],
      });
    } catch (err) {
      console.error("❌ Upload error:", err.response?.data || err.message);
      alert(err.response?.data?.message || "Upload failed");
    }
  };

  return (
    <>
      <Navbar />
      <section className="CourseUpload-hero">
        <h1 className="CourseUpload-title"> Upload Courses Form</h1>
        <p className="CourseUpload-subtitle">
          Admins can use this form to upload new courses to the platform.
        </p>
      </section>

      <Container maxWidth="md" sx={{ py: 6 }}>
        <Paper elevation={4} sx={{ p: 4, borderRadius: 4 }}>
          <Typography variant="h5" fontWeight={600} textAlign="center" mb={3}>
            📘 Course Upload Form
          </Typography>

          <form onSubmit={handleUpload}>

            <Typography variant="h6" color="primary" mb={2}>
              Course Information
            </Typography>
            <TextField
              label="Course Title"
              name="title"
              fullWidth
              margin="normal"
              value={formData.title}
              onChange={handleChange}
              required
            />

            <Typography variant="subtitle1" mt={2}>
              Course Level:
            </Typography>
            <RadioGroup
              row
              name="level"
              value={formData.level}
              onChange={handleChange}
            >

              <FormControlLabel value="Certificate" control={<Radio />} label="Certificate" />
            </RadioGroup>

            <Typography variant="subtitle1" mt={2}>
              Course Expertise:
            </Typography>
            <RadioGroup
              row
              name="expertise"
              value={formData.expertise}
              onChange={handleChange}
            >
              {["General Practitioner", "Physician", "Nursing", "Healthcare Provider"].map(
                (exp) => (
                  <FormControlLabel key={exp} value={exp} control={<Radio />} label={exp} />
                )
              )}
            </RadioGroup>

            <Grid container spacing={2}>
              <Grid item xs={12} sm={6}>
                <FormControl fullWidth sx={{ mt: 2 }}>
                  <InputLabel id="duration-label">Duration</InputLabel>
                  <Select
                    labelId="duration-label"
                    name="duration"
                    value={formData.duration}
                    onChange={handleChange}
                    label="Duration"
                    sx={{
                      height: "56px",
                      borderRadius: "8px",
                      backgroundColor: "#fff",
                    }}
                  >
                    {[...Array(12)].map((_, i) => (
                      <MenuItem key={i + 1} value={`${i + 1} months`}>
                        {i + 1} Month{i > 0 ? "s" : ""}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>

              </Grid>
              <Grid item xs={12} sm={6}>
                <Typography variant="subtitle1" mt={2}>
                  Mode of Delivery:
                </Typography>
                <RadioGroup
                  row
                  name="mode"
                  value={formData.mode}
                  onChange={handleChange}
                >
                  <FormControlLabel value="Online" control={<Radio />} label="100% Online" />
                  <FormControlLabel value="Hybrid" control={<Radio />} label="Hybrid" />
                  <FormControlLabel value="Offline" control={<Radio />} label="Offline" />
                </RadioGroup>
              </Grid>
            </Grid>

            <TextField
              label="Full Description (Learning Outcomes)"
              name="description"
              fullWidth
              multiline
              rows={4}
              margin="normal"
              value={formData.description}
              onChange={handleChange}
            />

            <Divider sx={{ my: 3 }} />


            <Typography variant="h6" color="primary" mb={2}>
              Course Details
            </Typography>

            <TextField
              label="Eligibility Criteria"
              name="eligibility"
              fullWidth
              margin="normal"
              value={formData.eligibility}
              onChange={handleChange}
            />

            <FormGroup sx={{ mt: 1 }}>
              <FormControlLabel
                control={
                  <Checkbox
                    checked={formData.certificate}
                    onChange={handleChange}
                    name="certificate"
                  />
                }
                label="Certificate Provided"
              />
            </FormGroup>

            <Divider sx={{ my: 3 }} />


            <Typography variant="h6" color="primary" mb={2}>
              Pricing & Enrollment
            </Typography>
            <Grid container spacing={2}>
              <Grid item xs={12} sm={6}>
                <TextField
                  label="Course Fee (INR)"
                  name="price"
                  fullWidth
                  type="number"
                  value={formData.price}
                  onChange={handleChange}
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField
                  label="Discount (%)"
                  name="discount"
                  fullWidth
                  type="number"
                  value={formData.discount}
                  onChange={handleChange}
                />
              </Grid>
            </Grid>

            <Divider sx={{ my: 3 }} />


            <Typography variant="h6" color="primary" mb={2}>
              Thumbnail Image Link
            </Typography>
            <TextField
              label="Thumbnail Image URL (e.g., https://...)"
              name="thumbnail"
              fullWidth
              margin="normal"
              required  // ✅ Add this
              value={formData.thumbnail}
              onChange={handleChange}
              placeholder="Paste your image link here"
            />

            <Typography variant="subtitle1" mt={2}>
              🎥 Course Videos (Drive Links)
            </Typography>
            {formData.videos.map((video, index) => (
              <TextField
                key={index}
                fullWidth
                margin="dense"
                placeholder="Enter Google Drive video link (Module -1; Lesson -1,2,3,4)"
                value={video}
                onChange={(e) => handleVideoChange(index, e.target.value)}
              />
            ))}
            <Button
              onClick={addVideoField}
              sx={{ mt: 1 }}
              variant="outlined"
              color="secondary"
            >
              + Add Another Video
            </Button>

            <Divider sx={{ my: 3 }} />


            <Typography variant="h6" color="primary" mb={2}>
              Admin Options
            </Typography>
            <Typography variant="subtitle1">Logo Placement:</Typography>
            <RadioGroup
              row
              name="logoPlacement"
              value={formData.logoPlacement}
              onChange={handleChange}
            >
              {["Top Left", "Center", "Custom"].map((pos) => (
                <FormControlLabel key={pos} value={pos} control={<Radio />} label={pos} />
              ))}
            </RadioGroup>

            <Typography variant="subtitle1">Status:</Typography>
            <RadioGroup
              row
              name="status"
              value={formData.status}
              onChange={handleChange}
            >
              {["Draft", "Published"].map((status) => (
                <FormControlLabel key={status} value={status} control={<Radio />} label={status} />
              ))}
            </RadioGroup>

            <Box textAlign="center" mt={4}>
              <Button
                type="submit"
                variant="contained"
                size="large"
                sx={{
                  px: 5,
                  py: 1.5,
                  background: "linear-gradient(135deg, #007bff, #00b4d8)",
                }}
              >
                Upload Course
              </Button>
            </Box>
          </form>
        </Paper>
      </Container>
      <Footer />
    </>
  );
};

export default CourseUpload;




{/*}
import React, { useState } from "react";
import axios from "axios";
import {
  Container,
  TextField,
  Typography,
  Box,
  Button,
  Grid,
  RadioGroup,
  FormControlLabel,
  Radio,
  Checkbox,
  FormGroup,
  Paper,
  Divider,
} from "@mui/material";
import Navbar from "./Navbar";
import Footer from "./Footer";
import "./CourseUpload.css";

const API = "https://course-selling-backend-2-rr1n.onrender.com";

const CourseUpload = () => {
  const [formData, setFormData] = useState({
    title: "",
    level: "",
    expertise: "",
    duration: "",
    mode: "",
    description: "",
    eligibility: "",
    certificate: false,
    price: "",
    discount: "",
    enrollmentLink: "",
    logoPlacement: "",
    status: "",
    thumbnail: "", // ✅ now stores image link instead of file
    videos: [""],
  });

  // ✅ Convert Google Drive links to embeddable preview format
  const handleVideoChange = (index, value) => {
    let finalLink = value.trim();
    if (finalLink.includes("drive.google.com")) {
      const fileIdMatch = finalLink.match(/\/d\/(.*?)\//);
      const fileIdParam = finalLink.match(/id=([^&]+)/);
      const fileId = fileIdMatch ? fileIdMatch[1] : fileIdParam ? fileIdParam[1] : null;
      if (fileId) finalLink = `https://drive.google.com/file/d/${fileId}/preview`;
    }
    const updated = [...formData.videos];
    updated[index] = finalLink;
    setFormData({ ...formData, videos: updated });
  };

  const addVideoField = () =>
    setFormData({ ...formData, videos: [...formData.videos, ""] });

  // ✅ Simplified handleChange (no file handling)
  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData({
      ...formData,
      [name]: type === "checkbox" ? checked : value,
    });
  };

  // ✅ Updated handleUpload (no FormData / file upload)
  const handleUpload = async (e) => {
    e.preventDefault();
    try {
      const token = localStorage.getItem("token");
      const payload = { ...formData, videos: JSON.stringify(formData.videos) };

      await axios.post(`${API}/api/courses/upload`, payload, {
        headers: {
          "x-auth-token": token,
          "Content-Type": "application/json",
        },
      });

      alert("✅ Course uploaded successfully!");
      setFormData({
        title: "",
        level: "",
        expertise: "",
        duration: "",
        mode: "",
        description: "",
        eligibility: "",
        certificate: false,
        price: "",
        discount: "",
        enrollmentLink: "",
        logoPlacement: "",
        status: "",
        thumbnail: "",
        videos: [""],
      });
    } catch (err) {
      console.error("❌ Upload error:", err.response?.data || err.message);
      alert(err.response?.data?.message || "Upload failed");
    }
  };

  return (
    <>
      <Navbar />
      <section className="CourseUpload-hero">
        <h1 className="CourseUpload-title"> Upload Courses Form</h1>
        <p className="CourseUpload-subtitle">
          Admins can use this form to upload new courses to the platform.
        </p>
      </section>

      <Container maxWidth="md" sx={{ py: 6 }}>
        <Paper elevation={4} sx={{ p: 4, borderRadius: 4 }}>
          <Typography variant="h5" fontWeight={600} textAlign="center" mb={3}>
            📘 Course Upload Form
          </Typography>

          <form onSubmit={handleUpload}>
           
            <Typography variant="h6" color="primary" mb={2}>
              Course Information
            </Typography>
            <TextField
              label="Course Title"
              name="title"
              fullWidth
              margin="normal"
              value={formData.title}
              onChange={handleChange}
              required
            />

            <Typography variant="subtitle1" mt={2}>
              Course Level:
            </Typography>
            <RadioGroup
              row
              name="level"
              value={formData.level}
              onChange={handleChange}
            >
        
              <FormControlLabel value="Certificate" control={<Radio />} label="Certificate" />
            </RadioGroup>

            <Typography variant="subtitle1" mt={2}>
              Course Expertise:
            </Typography>
            <RadioGroup
              row
              name="expertise"
              value={formData.expertise}
              onChange={handleChange}
            >
              {["General Practitioner", "Physician", "Nursing", "Healthcare Provider"].map(
                (exp) => (
                  <FormControlLabel key={exp} value={exp} control={<Radio />} label={exp} />
                )
              )}
            </RadioGroup>

            <Grid container spacing={2}>
              <Grid item xs={12} sm={6}>
                <TextField
                  label="Duration (e.g., 3, 6, 12 months)"
                  name="duration"
                  fullWidth
                  margin="normal"
                  value={formData.duration}
                  onChange={handleChange}
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <Typography variant="subtitle1" mt={2}>
                  Mode of Delivery:
                </Typography>
                <RadioGroup
                  row
                  name="mode"
                  value={formData.mode}
                  onChange={handleChange}
                >
                  <FormControlLabel value="Online" control={<Radio />} label="100% Online" />
                  <FormControlLabel value="Hybrid" control={<Radio />} label="Hybrid" />
                  <FormControlLabel value="Offline" control={<Radio />} label="Offline" />
                </RadioGroup>
              </Grid>
            </Grid>

            <TextField
              label="Full Description (Learning Outcomes)"
              name="description"
              fullWidth
              multiline
              rows={4}
              margin="normal"
              value={formData.description}
              onChange={handleChange}
            />

            <Divider sx={{ my: 3 }} />

           
            <Typography variant="h6" color="primary" mb={2}>
              Course Details
            </Typography>

            <TextField
              label="Eligibility Criteria"
              name="eligibility"
              fullWidth
              margin="normal"
              value={formData.eligibility}
              onChange={handleChange}
            />

            <FormGroup sx={{ mt: 1 }}>
              <FormControlLabel
                control={
                  <Checkbox
                    checked={formData.certificate}
                    onChange={handleChange}
                    name="certificate"
                  />
                }
                label="Certificate Provided"
              />
            </FormGroup>

            <Divider sx={{ my: 3 }} />

            
            <Typography variant="h6" color="primary" mb={2}>
              Pricing & Enrollment
            </Typography>
            <Grid container spacing={2}>
              <Grid item xs={12} sm={6}>
                <TextField
                  label="Course Fee (INR)"
                  name="price"
                  fullWidth
                  type="number"
                  value={formData.price}
                  onChange={handleChange}
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField
                  label="Discount (%)"
                  name="discount"
                  fullWidth
                  type="number"
                  value={formData.discount}
                  onChange={handleChange}
                />
              </Grid>
            </Grid>

            <Divider sx={{ my: 3 }} />

            
            <Typography variant="h6" color="primary" mb={2}>
              Thumbnail Image Link
            </Typography>
            <TextField
              label="Thumbnail Image URL (e.g., https://...)"
              name="thumbnail"
              fullWidth
              margin="normal"
              required  // ✅ Add this
              value={formData.thumbnail}
              onChange={handleChange}
              placeholder="Paste your image link here"
            />

            <Typography variant="subtitle1" mt={2}>
              🎥 Course Videos (Drive Links)
            </Typography>
            {formData.videos.map((video, index) => (
              <TextField
                key={index}
                fullWidth
                margin="dense"
                placeholder="Enter Google Drive video link (Module -1; Lesson -1,2,3,4)"
                value={video}
                onChange={(e) => handleVideoChange(index, e.target.value)}
              />
            ))}
            <Button
              onClick={addVideoField}
              sx={{ mt: 1 }}
              variant="outlined"
              color="secondary"
            >
              + Add Another Video
            </Button>

            <Divider sx={{ my: 3 }} />

           
            <Typography variant="h6" color="primary" mb={2}>
              Admin Options
            </Typography>
            <Typography variant="subtitle1">Logo Placement:</Typography>
            <RadioGroup
              row
              name="logoPlacement"
              value={formData.logoPlacement}
              onChange={handleChange}
            >
              {["Top Left", "Center", "Custom"].map((pos) => (
                <FormControlLabel key={pos} value={pos} control={<Radio />} label={pos} />
              ))}
            </RadioGroup>

            <Typography variant="subtitle1">Status:</Typography>
            <RadioGroup
              row
              name="status"
              value={formData.status}
              onChange={handleChange}
            >
              {["Draft", "Published"].map((status) => (
                <FormControlLabel key={status} value={status} control={<Radio />} label={status} />
              ))}
            </RadioGroup>

            <Box textAlign="center" mt={4}>
              <Button
                type="submit"
                variant="contained"
                size="large"
                sx={{
                  px: 5,
                  py: 1.5,
                  background: "linear-gradient(135deg, #007bff, #00b4d8)",
                }}
              >
                Upload Course
              </Button>
            </Box>
          </form>
        </Paper>
      </Container>
      <Footer />
    </>
  );
};

export default CourseUpload;



*/}










